import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAnalyticsStore from '../stores/useAnalyticsStore';
import useAuthStore from '../stores/useAuthStore';
import DashboardLayout from '../components/DashboardLayout';
import axiosClient from '../services/api';

const BadgesPage = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuthStore();
  const {
    badges,
    leaderboard,
    fetchBadges,
    fetchLeaderboard,
    isLoading: isAnalyticsLoading,
    error: analyticsError
  } = useAnalyticsStore();

  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('my-badges');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const userRes = await axiosClient.get('/badges/me/');
        setUser(userRes.data.user);
        await fetchBadges();
        await fetchLeaderboard();
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load data');
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  if (isLoading || isAnalyticsLoading) {
    return (
      <DashboardLayout userRole={authUser.role}>
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userRole={authUser.role}>
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
        <div className="max-w-5xl mx-auto px-4">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white mb-2">Achievements</h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              Earn badges by completing courses, acing quizzes, and more
            </p>
          </div>

          {error && analyticsError && (
            <div className="mb-6 p-3 bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300 rounded-lg text-sm">
              {error || analyticsError}
            </div>
          )}

          {/* Tabs */}
          <div className="mb-8 border-b border-neutral-200 dark:border-neutral-700">
            <nav className="-mb-px flex space-x-8">
              {['my-badges', 'all-badges', 'leaderboard'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                    activeTab === tab
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
                  }`}
                >
                  {tab.replace('-', ' ')}
                </button>
              ))}
            </nav>
          </div>

          {/* My Badges */}
          {activeTab === 'my-badges' && (
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Your Badges</h2>
              {user?.earnedBadges?.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
                  {user?.earnedBadges?.map((earnedBadge) => (
                    <div key={earnedBadge._id} className="text-center">
                      <img
                        src={`${process.env.REACT_APP_SOCKET_URL}/public${earnedBadge.badge?.icon}`}
                        alt={earnedBadge?.badge?.name}
                        className="w-20 h-20 mx-auto mb-2 rounded-full border-2 border-primary-500"
                      />
                      <p className="text-xs font-medium text-neutral-900 dark:text-white">
                        {earnedBadge?.badge?.name}
                      </p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        {earnedBadge?.badge?.points} pts
                      </p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        {new Date(earnedBadge?.earnedAt).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🎖️</div>
                  <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">No badges yet</h3>
                  <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                    Complete courses and ace quizzes to earn your first badge!
                  </p>
                  <button
                    onClick={() => navigate('/student/my-courses')}
                    className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm"
                  >
                    My Courses
                  </button>
                </div>
              )}
            </div>
          )}

          {/* All Badges */}
          {activeTab === 'all-badges' && (
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">All Badges</h2>
              {badges?.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
                  {badges?.map((badge) => {
                    const isEarned = user?.earnedBadges?.some(
                      eb => eb?.badge?._id.toString() === badge?._id.toString()
                    );
                    return (
                      <div
                        key={badge?._id}
                        className={`text-center p-4 rounded-lg ${
                          isEarned
                            ? 'bg-primary-50 dark:bg-primary-900/20 border-2 border-primary-500'
                            : 'bg-neutral-50 dark:bg-neutral-700/50 border-2 border-neutral-200 dark:border-neutral-700'
                        }`}
                      >
                        <img
                          src={`${process.env.REACT_APP_SOCKET_URL}/public${badge?.icon}`}
                          alt={badge.name}
                          className="w-20 h-20 mx-auto mb-2 rounded-full"
                        />
                        <p className="text-xs font-medium text-neutral-900 dark:text-white">
                          {badge?.name}
                        </p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">
                          {badge?.points} pts
                        </p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                          {badge?.description}
                        </p>
                        {!isEarned && (
                          <p className="text-xs text-warning-500 dark:text-warning-400 mt-2">Locked</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🏅</div>
                  <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
                    No badges available
                  </h3>
                  <p className="text-neutral-600 dark:text-neutral-400">
                    Check back later for new achievements!
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Leaderboard */}
          {activeTab === 'leaderboard' && (
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Top Students</h2>
              {leaderboard?.length > 0 ? (
                <div className="space-y-4">
                  {leaderboard.map((student, index) => (
                    <div
                      key={student._id}
                      className={`flex items-center p-4 rounded-lg ${
                        index === 0
                          ? 'bg-gradient-to-r from-yellow-100 to-yellow-50 dark:from-yellow-900/30 dark:to-yellow-900/20 border-2 border-yellow-400'
                          : index === 1
                            ? 'bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800/30 dark:to-gray-800/20 border-2 border-gray-400'
                            : index === 2
                              ? 'bg-gradient-to-r from-amber-100 to-amber-50 dark:from-amber-900/30 dark:to-amber-900/20 border-2 border-amber-400'
                              : 'bg-neutral-50 dark:bg-neutral-700/50'
                      }`}
                    >
                      <div className="w-10 h-10 bg-primary-500 dark:bg-primary-400 rounded-full flex items-center justify-center mr-4">
                        <span className="text-white font-bold">{index + 1}</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-neutral-900 dark:text-white">
                          {student.name}
                        </h3>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400">
                          {student.totalBadges} badges • {student.totalPoints} points
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                          {student.totalPoints}
                        </div>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">pts</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🏆</div>
                  <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
                    Leaderboard empty
                  </h3>
                  <p className="text-neutral-600 dark:text-neutral-400">
                    Be the first to earn badges and top the leaderboard!
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BadgesPage;